import { InputBase } from '@mui/material';
import { useEffect, useRef } from 'react';

type EditableTranscriptionWordProps = {
  draftText: string;
  onDraftChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
};

/**
 * Inline text input for editing a single transcript word; Enter saves, Esc cancels.
 * On open, focuses the field and places the caret at the end of the text.
 * Width tracks the draft text so the field stays roughly the size of the word.
 */
export function EditableTranscriptionWord({
  draftText,
  onDraftChange,
  onSave,
  onCancel,
  isSaving,
}: EditableTranscriptionWordProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWidthCh = Math.max(draftText.length, 1);

  useEffect(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    input.focus();
    const caretPosition = input.value.length;
    input.setSelectionRange(caretPosition, caretPosition);
  }, []);

  return (
    <InputBase
      inputRef={inputRef}
      value={draftText}
      disabled={isSaving}
      onChange={(event) => onDraftChange(event.target.value)}
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        event.stopPropagation();

        if (event.key === 'Enter') {
          event.preventDefault();
          onSave();
        }

        if (event.key === 'Escape') {
          event.preventDefault();
          onCancel();
        }
      }}
      sx={{
        display: 'inline-flex',
        width: `${inputWidthCh}ch`,
        typography: 'body2',
        verticalAlign: 'baseline',
        borderBottom: 1,
        borderColor: 'primary.main',
        '& .MuiInputBase-input': {
          p: 0,
          width: '100%',
        },
      }}
    />
  );
}
